// ----------------------------------------------------------------------------
// Component: Fork Carousel
// ----------------------------------------------------------------------------


// -------------------------------------
// Dependencies
// -------------------------------------
import Swiper, { Pagination, A11y, Keyboard, EffectCoverflow, Thumbs } from 'swiper';
import 'snippets/icon/icon';

// Style includes
import 'snippets/swiper-control/swiper-control';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import 'swiper/css/effect-coverflow';
import 'swiper/css/thumbs';
import './_fork-carousel.scss';


// -------------------------------------
// Variables
// -------------------------------------
const CAROUSEL_SELECTOR = '.carousel--fork';
const CAROUSEL_CLASS = 'carousel--fork';
const CAROUSEL_SECTION_SELECTOR = '.fork-carousel__wrapper';
const CAROUSEL_PAGINATION_SELECTOR = '.swiper-pagination';
const CAROUSEL_SLIDE_SELECTOR = '.swiper-slide';
const CAROUSEL_NAVIGATION_SELECTOR = '.carousel--fork-navigation';
const CAROUSEL_NAVIGATION_CLASS = 'carousel--fork-navigation';


const GLOW_EFFECT_SELECTOR = '.carousel-section--fork';

// Constants for thresholds
const TABLET_THRESHOLD = 3;
const DESKTOP_THRESHOLD = 5;

// -------------------------------------
// Hooks
// -------------------------------------

function isEdgeBrowser() {
    return /Edg/.test(navigator.userAgent);
}

function isSafari() {
    const userAgent = navigator.userAgent.toLowerCase();
    return (
        userAgent.includes('safari') &&
        !userAgent.includes('chrome') &&
        !userAgent.includes('firefox') &&
        !(userAgent.includes('android') || userAgent.includes('iphone') || userAgent.includes('ipad'))
    );
}

addEventListener('DOMContentLoaded', function() {
    // Add class if the browser is Edge
    if (isEdgeBrowser() && document.querySelector(CAROUSEL_SECTION_SELECTOR) !== null) {
        document.body.classList.add('edge-browser');
    }

    document.querySelectorAll(CAROUSEL_SECTION_SELECTOR).forEach((element, index) => {
        // -------------------------------------------------
        // Initialize selectors and unique class for each el
        // -------------------------------------------------
        // Fork Cards
        const carouselSelector = element.querySelector(CAROUSEL_SELECTOR);
        const paginationSelector = element.querySelector(CAROUSEL_PAGINATION_SELECTOR);
        const slides = carouselSelector.querySelectorAll(CAROUSEL_SLIDE_SELECTOR);
    
        carouselSelector?.classList.add(`${CAROUSEL_CLASS}-${index}`);
        paginationSelector?.classList.add(`${CAROUSEL_CLASS}-pagination-${index}`);
    
        // Top navigation (size)
        const carouselNavSelector = element.querySelector(CAROUSEL_NAVIGATION_SELECTOR);
        const slidesNav = carouselNavSelector.querySelectorAll(CAROUSEL_SLIDE_SELECTOR);
        carouselNavSelector?.classList.add(`${CAROUSEL_NAVIGATION_CLASS}-${index}`);
    
        // Initialize counters and variables
        let isSwiperForkSlided = false;
        let isSwiperNavSlided = false;
        let middleSlideCounter = Math.ceil(slidesNav.length / 2);
        const slidesPerViewTablet = Math.min(slides.length, TABLET_THRESHOLD);
        const slidesPerViewDesktop = Math.min(slides.length, DESKTOP_THRESHOLD);
    
        let initialSlide = middleSlideCounter > 1 ? middleSlideCounter : 0;

        // Check slides length and apply classes accordingly
        if (slides.length < TABLET_THRESHOLD) {
            carouselSelector.classList.add('carousel--force-width-tablet');
            carouselSelector.classList.add('carousel--grid-tablet-' + slides.length);
        }

        if (slides.length < DESKTOP_THRESHOLD) {
            carouselSelector.classList.add('carousel--force-width-desktop');
            carouselSelector.classList.add('carousel--grid-desktop-' + slides.length);
        }
    
        // -------------------------
        // Initialize Fork Swiper
        // -------------------------
        const swiper = new Swiper(`.${CAROUSEL_CLASS}-${index}`, {
            modules: [Pagination, A11y, Keyboard, EffectCoverflow, Thumbs],
            slidesPerView: 1.5,
            spaceBetween: 15,
            observer: true,
            observeParents: true,
            a11y: true,
            watchOverflow: false,
            centeredSlides: true,
            grabCursor: true,
            autoHeight: false,
            centerInsufficientSlides: true,
            initialSlide: initialSlide,
    
            effect: 'coverflow', // https://swiperjs.com/swiper-api#coverflow-effect
            coverflowEffect: {
                rotate: 0,
                stretch: -30,
                depth: 100,
                modifier: 1,
                slideShadows: false,
            },
            on: {
                init: function () {
                    carouselEffectSafari(this);
                },
                slideChange: function () {
                    carouselEffectSafari(this);
                },
            },

            breakpoints: {
                680: {
                    slidesPerView: slidesPerViewTablet,
                    spaceBetween: 0,
    
                    coverflowEffect: {
                        stretch: -100,
                    },
                },
                1024: {
                    slidesPerView: slidesPerViewDesktop,
                    spaceBetween: 0,
    
                    coverflowEffect: {
                        stretch: -100,
                    },
                },
                1280: {
                    slidesPerView: slidesPerViewDesktop,
                    spaceBetween: 0,
                    coverflowEffect: {
                        stretch: -150,
                    },
                },
                1600: {
                    slidesPerView: slidesPerViewDesktop,
                    spaceBetween: 0,
                    coverflowEffect: {
                        stretch: -200,
                    },
                }
            },
        });
    
        swiper.on('click', function () {
            swiper.slideTo(swiper.clickedIndex)
            handleSwiperSlideChange(swiperNav, slidesNav, swiper);
        });
    
        // Handle activeIndexChange event when the Fork slider changes slide
        swiper.on('activeIndexChange', function () {
            isSwiperForkSlided = true;
    
            if (!isSwiperNavSlided) {
                handleSwiperSlideChange(swiperNav, slidesNav, swiper)
            }
    
            isSwiperNavSlided = false; // Reset the flag
        });
    
        swiper.slides?.forEach((slide, slideIndex) => {
            const slideLink = slide.querySelector('a');
            slideLink?.addEventListener('focus', () => swiper.slideTo(slideIndex));
        });
        swiper.init();
    
        // -------------------------------------
        // Initialize Top navigation (size) Swiper
        // -------------------------------------
        // Calculate initialSlideNav and other parameters
        let slidesPerViewMobileNav = Math.min(slidesNav.length, 3.2);
        let slidesPerViewTabletNav = Math.min(slidesNav.length, 4.5);
        let slidesPerViewDesktopNav = Math.min(slidesNav.length, 10);
        const initialSlideNav = findInitialSlideNav(slidesNav, swiper);
    
        const swiperNav = new Swiper(`.${CAROUSEL_NAVIGATION_CLASS}-${index}`, {
            modules: [A11y, Keyboard],
            slidesPerView: slidesPerViewMobileNav,
            spaceBetween: 30,
            observer: true,
            observeParents: true,
            a11y: true,
            watchOverflow: false,
            centeredSlides: true,
            grabCursor: true,
            centerInsufficientSlides: true,
            initialSlide: initialSlideNav,
    
            breakpoints: {
                680: {
                    slidesPerView: slidesPerViewTabletNav
                },
                1024: {
                    slidesPerView: slidesPerViewDesktopNav
                }
            },
        });
    
        // Handle click event for swiperNav
        swiperNav.on('click', function () {
            swiperNav.slideTo(swiperNav.clickedIndex)
            handleSwiperNavClick(swiperNav, slides, swiper);
        });
    
        swiperNav.on('activeIndexChange', function () {
            isSwiperNavSlided = true;
    
            if (!isSwiperForkSlided) {
                handleSwiperNavClick(swiperNav, slides, swiper);
            }
    
            isSwiperForkSlided = false; // Reset the flag
        });
    });

    function carouselEffectSafari(swiper) {
        if (!isSafari()) return;
    
        swiper.wrapperEl.style.transformStyle = 'inherit';
        const { slides, activeIndex } = swiper;
        
        slides.forEach((slide, index) => {
            const distance = Math.abs(index - activeIndex);
            let scale, positionX;
            
            // Define scale ratios and translation based on distance from the active slide
            switch (distance) {
                case 0:
                    scale = 1;
                    positionX = '0';
                    break;
                case 1:
                    scale = 0.92;
                    positionX = '-1.8%';
                    break;
                case 2:
                    scale = 0.80;
                    positionX = '-3.5%';
                    break;
                default:
                    scale = 0.70;
                    positionX = '-4.5%';
                    break;
            }
            
            // Apply scale and translateX transformations
            slide.style.scale = scale;
    
            // Determine the appropriate side positioning based on the slide's index
            if (index === activeIndex) {
                slide.style.left = 'auto';
                slide.style.right = 'auto';
            } else {
                slide.style.left = index > activeIndex ? positionX : 'auto';
                slide.style.right = index < activeIndex ? positionX : 'auto';
            }
        });
    }
    
    function handleSwiperSlideChange(swiperNav, slidesNav, swiper) {
        const activeSlide = swiper.slides[swiper.activeIndex];
        const dataSize = activeSlide?.dataset.size;
        let activeIndex = 0;
        slidesNav.forEach((slide, index) => {
            if (activeIndex === 0) { // Check if activeIndex has not been set yet
                const dataSlide = slide?.dataset.slide;
                if (dataSlide === dataSize) {
                    // Store the order number of the first matching slide
                    activeIndex = index + 1; // Index should start from 1
                }
            }
        });
    
        activeIndex = Math.max(activeIndex - 1, 0); // Ensure activeIndex is not negative
    
        swiperNav.slideTo(activeIndex);
    }
    
    function handleSwiperNavClick(swiperNav, slides, swiper) {
        const activeSlide = swiperNav.slides[swiperNav.activeIndex];
        const dataSlide = activeSlide?.dataset.slide;
        let activeIndex = 0;
        slides.forEach((slide, index) => {
            if (activeIndex === 0) { // Check if activeIndex has not been set yet
                const dataSize = slide?.dataset.size;
                if (dataSlide === dataSize) {
                    // Store the order number of the first matching slide
                    activeIndex = index + 1; // Index should start from 1
                }
            }
        });
    
        activeIndex = Math.max(activeIndex - 1, 0); // Ensure activeIndex is not negative
    
        swiper.slideTo(activeIndex);
    }
    
    
    function findInitialSlideNav(slidesNav, swiper) {
        const activeSlide = swiper.slides[swiper.activeIndex];
        const dataSize = activeSlide?.dataset.size;
        let initialSlideNav = 0;
    
        slidesNav.forEach((slide, index) => {
            const dataSlide = slide?.dataset.slide;
            if (dataSlide === dataSize) {
                // Store the order number of the middle slide
                initialSlideNav = index + 1; // Index should start from 1
            }
        });
    
        return Math.max(initialSlideNav - 1, 0); // Ensure initialSlideNav is not negative
    }
    
    
    document.querySelectorAll(GLOW_EFFECT_SELECTOR)?.forEach((element, index) => {
        // Store the original background value
        const originalBackground = element.style.background;
    
        // Add transition for smooth effect
        element.style.transition = 'background 0.3s ease';
    
        // Mousemove event listener
        element.addEventListener('mousemove', (event) => {
            const buttonRect = element.getBoundingClientRect();
            const xPos = event.clientX - buttonRect.left;
            const yPos = event.clientY - buttonRect.top;
    
            // Apply the new background with radial gradient
            element.style.background = `radial-gradient(30% 30% at ${xPos}px ${yPos}px, rgba(53, 53, 53, 0.65) 0%, rgba(208, 208, 208, 0.00) 100%)`;
        });
    
        // Mouseleave event listener
        element.addEventListener('mouseleave', (event) => {
            // Calculate the position where the mouse leaves the container
            const buttonRect = element.getBoundingClientRect();
            const xPos = event.clientX - buttonRect.left;
            const yPos = event.clientY - buttonRect.top;
    
            // Apply the default radial gradient with transition from the mouse leave position
            element.style.background = `radial-gradient(30% 30% at ${xPos}px ${yPos}px, rgba(53, 53, 53, 0.65) 0%, rgba(208, 208, 208, 0.00) 100%)`;
    
            // After a short delay, transition back to the original background
            setTimeout(() => {
                element.style.background = originalBackground;
            }, 200); // Adjust the delay time (in milliseconds) as needed
        });
    });
});